import type { ReactNode } from 'react';
import { Instagram, Linkedin, Tiktok } from 'react-bootstrap-icons';
import type { z } from 'zod';

import { INSTAGRAM_PREFIX, LINKEDIN_PREFIX, TIKTOK_PREFIX, X_PREFIX } from '@repo/common';
import { DataTestIds } from '@repo/frontend-shared';
import { SDSIcon } from '@repo/sds/SDSIcon';
import { EmailIcon } from '@repo/ui/assets/icons/EmailIcon';
import { TwitterIcon } from '@repo/ui/assets/icons/TwitterIcon';
import type { userProfileFormSchema } from '@repo/ui/constants/zod-schemas';
import type { FormConfig } from '@repo/ui/fixtures/form/components/Form/Form';
import type { FormFieldConfig } from '@repo/ui/fixtures/form/components/FormField/FormField';

type FormValues = z.infer<typeof userProfileFormSchema>;

const EMAIL_FIELD: FormFieldConfig<FormValues> = {
  componentType: 'FormTextInput',
  componentProps: {
    name: 'email',
    label: 'Email',
    isRequired: true,
    type: 'email',
    placeholder: 'Enter your email',
    rightIcon: <EmailIcon />,
    dataTestId: DataTestIds.PROFILE.EMAIL,
    errorMessageTestId: DataTestIds.PROFILE.EMAIL_ERROR,
  },
};

const NAME_FIELDS: FormFieldConfig<FormValues>[] = [
  {
    componentType: 'FormTextInput',
    componentProps: {
      name: 'firstName',
      label: 'First Name',
      isRequired: true,
      placeholder: 'Enter your first name',
      dataTestId: DataTestIds.PROFILE.FIRST_NAME,
      errorMessageTestId: DataTestIds.PROFILE.FIRST_NAME_ERROR,
    },
    width: '50%',
  },
  {
    componentType: 'FormTextInput',
    componentProps: {
      name: 'lastName',
      label: 'Last Name',
      isRequired: true,
      placeholder: 'Enter your last name',
      dataTestId: DataTestIds.PROFILE.LAST_NAME,
      errorMessageTestId: DataTestIds.PROFILE.LAST_NAME_ERROR,
    },
    width: '50%',
  },
];

const EXPERIENCE_AND_SOCIALS: FormConfig<FormValues> = [
  {
    groupTitle: 'Experience',
    groupClassName: 'UserProfile__formGroup',
    fields: [
      {
        componentType: 'FormTextInput',
        componentProps: {
          name: 'company',
          label: 'Current Company',
          isRequired: true,
          placeholder: 'Ex. Apple',
          dataTestId: DataTestIds.PROFILE.COMPANY,
          errorMessageTestId: DataTestIds.PROFILE.COMPANY_ERROR,
        },
        width: '50%',
      },
      {
        componentType: 'FormTextInput',
        componentProps: {
          name: 'position',
          label: 'Position',
          isRequired: true,
          placeholder: 'Ex. Product Manager',
          dataTestId: DataTestIds.PROFILE.POSITION,
          errorMessageTestId: DataTestIds.PROFILE.POSITION_ERROR,
        },
        width: '50%',
      },
      {
        componentType: 'FormTextAreaInput',
        componentProps: {
          name: 'description',
          isRequired: true,
          label: 'About me',
          placeholder: 'Write a few words about your relevant experience.',
          dataTestId: DataTestIds.PROFILE.ABOUT,
        },
      },
    ],
  },
  {
    groupTitle: 'Socials',
    groupClassName: 'UserProfile__formGroup',
    fields: [
      {
        componentType: 'FormTextInput',
        componentProps: {
          name: 'linkedin',
          label: 'LinkedIn Profile',
          isRequired: true,
          rightIcon: <SDSIcon icon={<Linkedin />} size="big" color="inactive" />,
          placeholder: LINKEDIN_PREFIX,
          dataTestId: DataTestIds.PROFILE.LINKEDIN,
          errorMessageTestId: DataTestIds.PROFILE.LINKEDIN_ERROR,
        },
      },
      {
        componentType: 'FormTextInput',
        componentProps: {
          name: 'x',
          label: 'X Profile',
          rightIcon: <TwitterIcon />,
          placeholder: X_PREFIX,
          dataTestId: DataTestIds.PROFILE.X,
          errorMessageTestId: DataTestIds.PROFILE.X_ERROR,
        },
      },
      {
        componentType: 'FormTextInput',
        componentProps: {
          name: 'instagram',
          label: 'Instagram Profile',
          rightIcon: <SDSIcon icon={<Instagram />} size="big" color="inactive" />,
          placeholder: INSTAGRAM_PREFIX,
          dataTestId: DataTestIds.PROFILE.INSTAGRAM,
          errorMessageTestId: DataTestIds.PROFILE.INSTAGRAM_ERROR,
        },
      },
      {
        componentType: 'FormTextInput',
        componentProps: {
          name: 'tiktok',
          label: 'TikTok Profile',
          rightIcon: <SDSIcon icon={<Tiktok />} size="big" color="inactive" />,
          placeholder: TIKTOK_PREFIX,
          dataTestId: DataTestIds.PROFILE.TIKTOK,
          errorMessageTestId: DataTestIds.PROFILE.TIKTOK_ERROR,
        },
      },
    ],
  },
];

/**
 * The profile form. Member settings place the phone number and SMS consent after the email, which
 * then shares its row with the phone unless the fields are stacked.
 */
export function buildUserProfileFormFields(phoneSlot?: ReactNode, isStacked = false): FormConfig<FormValues> {
  return [
    {
      groupTitle: 'Personal Information',
      groupSubtitle: 'Make sure your profile contains accurate information.',
      groupClassName: 'UserProfile__formGroup',
      fields: [...NAME_FIELDS, phoneSlot && !isStacked ? { ...EMAIL_FIELD, width: '50%' } : EMAIL_FIELD],
      extraContent: phoneSlot,
    },
    {
      groupTitle: 'Profile preferences',
      groupClassName: 'UserProfile__formGroup',
      fields: [
        {
          componentType: 'FormTextInput',
          componentProps: {
            name: 'tagline',
            label: 'Tagline',
            placeholder: 'In one line, describe who you are',
            dataTestId: DataTestIds.PROFILE.TAGLINE,
          },
        },
        { componentType: 'FormTimezoneInput', componentProps: { name: 'timezone', label: 'Timezone' } },
      ],
    },
    ...EXPERIENCE_AND_SOCIALS,
  ];
}
